import { Rndm } from './Rndm.js';
import { RGBA } from './Farb.js';

import { css_manager_type } from './Css.js';
import { scrn_type } from './Scrn.js';

export interface wbgl_type {
    id: string;
    canvas: HTMLCanvasElement;
    context: WebGL2RenderingContext;
    clear: () => void;
    background_update: (color: RGBA) => void;
    close: () => void;
}

export function make_canvas(param: { Scrn?: scrn_type, parent?: HTMLElement, Css?: css_manager_type, background?: RGBA, depth?: boolean, stencil?: boolean, id?: string, preserve?: boolean }): wbgl_type {

    const { Css, Scrn, parent, background = [0.5, 0.1, 0.1, 1], depth = false, stencil = false, id = Rndm.id(), preserve = false } = param;

    const element_mode = !Css;
    const host = parent || (Scrn ? Scrn.dom : undefined);
    if (!host) throw new Error("make_canvas: provide either Css+Scrn or a parent element.");

    let canvas: HTMLCanvasElement;
    if (Css) {
        canvas = Css.make_canvas_fullscreen({ id, parent: host });
    } else {
        canvas = document.createElement('canvas');
        canvas.id = id;
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        host.appendChild(canvas);
    }

    //Menuclick nicht erlaubt
    canvas.addEventListener('contextmenu', function (event: MouseEvent) { event.preventDefault() });

    // Get the display density scale factor
    function sizing() {
        const w = element_mode ? host!.clientWidth : window.innerWidth;
        const h = element_mode ? host!.clientHeight : window.innerHeight;

        if (!element_mode) {
            canvas.style.width = w + "px";
            canvas.style.height = h + "px";
        }

        // Set actual drawing buffer size (scaled for high DPI)
        const scale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(w * scale);
        canvas.height = Math.floor(h * scale);
    }
    sizing();

    if (element_mode) {
        const ro = new ResizeObserver(sizing);
        ro.observe(host);
    }

    // Initialize WebGL context
    const options = {
        stencil: stencil, //maks
        antialias: true,
        premultipliedAlpha: false,
        alpha: false,
        //keep the color buffer between frames so callers can draw over previous frames
        //(trail effects). Default false preserves the original clear-each-composite behavior.
        preserveDrawingBuffer: preserve
    };

    const context = canvas.getContext("webgl2", options) as WebGL2RenderingContext;
    context.clearColor(background[0], background[1], background[2], background[3]);

    // deepseek wanted to add this when trying font texture rendering
    depth ? context.enable(context.DEPTH_TEST) : context.disable(context.DEPTH_TEST);
    context.enable(context.BLEND);
    context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);

    console.log("WebGL initialized:", context.getParameter(context.VERSION));

    const self = {
        id,
        canvas,
        context,
        clear: function () {
            context.clear(context.COLOR_BUFFER_BIT | context.DEPTH_BUFFER_BIT);
        },
        background_update: function (color: RGBA) {
            context.clearColor(color[0], color[1], color[2], color[3]);
        },
        close: function () {
            const extension = context.getExtension('WEBGL_lose_context');
            if (extension) extension.loseContext();
            canvas.remove();
        }
    };

    return self;
}

export function make_texture(gl: WebGL2RenderingContext, param: { src?: string, placeholder?: [number, number, number, number], on_load?: (img: HTMLImageElement) => void } = {}): WebGLTexture {

    const { src, placeholder = [30, 30, 30, 255], on_load } = param;

    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(placeholder));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    if (src) {
        const img = new Image();
        img.src = src;
        img.onload = () => {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
            if (on_load) on_load(img);
        };
    }

    return texture;
}